import PropTypes from 'prop-types'
import React, { useContext, useReducer } from 'react'

import { trackEvent } from 'utils/useGTM'
import { BasicEmotion, BasicMood, Emotion, ExistingGroupWithTags } from 'db-tables'

export const SET_ANGLE = 'SET_ANGLE'
export const RESET_WORDS = 'RESET_WORDS'
export const SET_JOURNAL = 'SET_JOURNAL'
export const SET_ANONYMOUS = 'SET_ANONYMOUS'
export const SET_EXISTING_MOODS = 'SET_EXISTING_MOODS'
export const SET_PLOTTED_MOODS = 'SET_PLOTTED_MOODS'
export const REMOVE_EMOTION_WORD = 'REMOVE_EMOTION_WORD'
export const TOGGLE_EMOTION_WORD = 'TOGGLE_EMOTION_WORD'
export const SET_ANGLE_FROM_WORD_SELECTION = 'SET_ANGLE_FROM_WORD_SELECTION'
export const RESET_ANONYMITY = 'RESET_ANONYMITY'

export const MAX_WORDS = 3
export const PLAN_LIMITED_WORDS = 1
export const INITIAL_DIAL_ANGLE = 90

type MoodPlotState = {
  angle: number | null
  angleChangedFromWordSelection?: boolean
  emotionWords: (Emotion | BasicEmotion)[]
  moods: BasicMood[]
  limited: boolean
  anon: boolean
  initialAnon: boolean
  displayName: string | null
  journalContent: string
  existingMoods: number
  plottedMoods: string[]
  emotionsLimit: number
}
type Action =
  | { type: 'SET_ANGLE'; angle: number }
  | { type: 'SET_ANONYMOUS'; anon: boolean; anonUsername: string | null }
  | { type: 'SET_ANGLE_FROM_WORD_SELECTION'; angle: number }
  | { type: 'SET_EXISTING_MOODS'; existingMoods: number }
  | { type: 'SET_PLOTTED_MOODS'; plottedMoods: string[] }
  | { type: 'RESET_WORDS' }
  | { type: 'RESET_ANONYMITY' }
  | { type: 'SET_JOURNAL'; journalContent: string }
  | { type: 'TOGGLE_EMOTION_WORD'; word: Emotion | BasicEmotion; angle: number }
  | { type: 'REMOVE_EMOTION_WORD'; wordId: string }

const initialState = (initialAnon: boolean, group: ExistingGroupWithTags): MoodPlotState => ({
  angle: null,
  angleChangedFromWordSelection: false,
  // FIXME: Pick only one type of emotions to be kept in the state - either mood or word
  // At the moment we keep the `emotionWords` only for their `emotionWords.name` property
  // We also keep `moods` to contain the angle at which this emotion has been set.
  emotionWords: [],
  moods: [],
  limited: false,
  initialAnon,
  anon: initialAnon,
  displayName: null,
  journalContent: '',
  existingMoods: 0,
  plottedMoods: [],
  emotionsLimit: effectiveLimit(group),
})

function effectiveLimit(group: ExistingGroupWithTags): number {
  return group.owner.permissions.emotions_per_submission_count
}

function isLimited(count: number, emotionsLimit: number, existingMoods: number) {
  return count + existingMoods >= emotionsLimit
}

function reducer(state: MoodPlotState, action: Action): MoodPlotState {
  switch (action.type) {
    case SET_ANGLE:
      return { ...state, angle: action.angle, angleChangedFromWordSelection: false }
    case SET_ANGLE_FROM_WORD_SELECTION:
      return { ...state, angle: action.angle, angleChangedFromWordSelection: true }
    case SET_JOURNAL: {
      // Trigger event only once
      if (state.journalContent === '' && action.journalContent !== '') {
        trackEvent({ event: 'AddJournalContent' })
      }
      return { ...state, journalContent: action.journalContent }
    }
    case RESET_WORDS:
      return { ...state, limited: false, emotionWords: [], moods: [] }
    case RESET_ANONYMITY:
      return { ...state, anon: state.initialAnon, displayName: null }
    case SET_ANONYMOUS:
      return { ...state, anon: action.anon, displayName: action.anonUsername }
    case SET_PLOTTED_MOODS:
      return { ...state, plottedMoods: action.plottedMoods }
    case SET_EXISTING_MOODS:
      return { ...state, existingMoods: action.existingMoods }
    case REMOVE_EMOTION_WORD: {
      trackEvent({ event: 'DeselectEmotionWord' })
      const emotionWords = state.emotionWords.filter((word) => word.id !== action.wordId)
      const moods = state.moods.filter((mood) => mood.emotion_id !== action.wordId)
      return {
        ...state,
        limited: isLimited(moods.length, state.emotionsLimit, state.existingMoods),
        emotionWords,
        moods,
      }
    }
    case TOGGLE_EMOTION_WORD: {
      // Check if the given word is already selected
      const isSelected = state.moods.some((mood) => mood.emotion_id === action.word.id)
      if (isSelected) {
        // Remove the word
        trackEvent({ event: 'DeselectEmotionWord' })
        const emotionWords = state.emotionWords.filter((word) => word.id !== action.word.id)
        const moods = state.moods.filter((mood) => mood.emotion_id !== action.word.id)
        return {
          ...state,
          limited: isLimited(moods.length, state.emotionsLimit, state.existingMoods),
          emotionWords,
          moods,
        }
      }
      // Check if we're over the limit
      if (state.limited) {
        return state
      }

      // Add the word
      trackEvent({ event: 'SelectEmotionWord', emotion: action.word.name })
      return {
        ...state,
        // Reflect the circle
        angle: action.angle,
        angleChangedFromWordSelection: true,
        emotionWords: [...state.emotionWords, action.word],
        moods: [
          ...state.moods,
          {
            id: action.word.id,
            angle: action.angle ?? 0,
            emotion_id: action.word.id,
            anon_user_name: state.displayName,
          },
        ],
        limited: isLimited(state.moods.length + 1, state.emotionsLimit, state.existingMoods),
        journalContent: state.journalContent,
      }
    }
    default:
      return state
  }
}

const Context = React.createContext<[MoodPlotState, React.Dispatch<Action>]>(null as any)

export default function MoodPlotProvider({
  group,
  initialAnon,
  children,
}: {
  group: ExistingGroupWithTags
  initialAnon: boolean
  children?: React.ReactNode
}) {
  const context = useReducer(reducer, initialState(initialAnon, group))
  return <Context.Provider value={context}>{children}</Context.Provider>
}
MoodPlotProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
}

export const useProvider = () => {
  const [state, dispatch] = useContext(Context)

  return {
    state,
    setAngle: (angle: number) => dispatch({ type: SET_ANGLE, angle }),
    resetAngle: () =>
      dispatch({ type: SET_ANGLE_FROM_WORD_SELECTION, angle: INITIAL_DIAL_ANGLE - 25 }),
    setAngleFromWordSelection: (angle: number) =>
      dispatch({ type: SET_ANGLE_FROM_WORD_SELECTION, angle }),
    setPlottedMoods: (plottedMoods: string[]) =>
      dispatch({ type: SET_PLOTTED_MOODS, plottedMoods }),
    setExistingMoods: (existingMoods: number) =>
      dispatch({ type: SET_EXISTING_MOODS, existingMoods }),
    setAnonymous: (anon: boolean, anon_user_name: string | null = null) =>
      dispatch({ type: SET_ANONYMOUS, anon, anonUsername: anon_user_name }),
    toggleEmotionWord: (word: Emotion | BasicEmotion, angle: number) =>
      dispatch({ type: TOGGLE_EMOTION_WORD, word, angle }),
    removeEmotionWord: (wordId: string) => dispatch({ type: REMOVE_EMOTION_WORD, wordId }),
    resetWords: () => dispatch({ type: RESET_WORDS }),
    resetAnonymity: () => dispatch({ type: RESET_ANONYMITY }),
    setJournal: (journalContent: string) => dispatch({ type: SET_JOURNAL, journalContent }),
  }
}
